import AppHeader from "@/components/AppHeader";
import { useCapabilities } from "@/hooks/useCapabilities";
import { useTenant } from "@/hooks/useTenant";
import { useUserRole } from "@/hooks/useUserRole";
import { UserManagement } from "@/pages/AdminUsers/UserManagement";
import { GoogleConnectionSection } from "@/pages/GoogleConnectionSection";
import { DashboardManagement } from "@/pages/Settings/DashboardManagement";
import { PropertyManagement } from "@/pages/Settings/PropertyManagement";
import { SettingsConfigTab } from "@/pages/Settings/SettingsConfigTab";
import { SettingsHowTo } from "@/pages/Settings/SettingsHowTo";
import { SparkConfigSection } from "@/pages/SparkConfigSection";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@simplicity/ui";
import { ArrowLeft } from "lucide-react";
import { useEffect } from "react";
import { Link, useNavigate, useSearchParams } from "react-router";

/** SIM-3113: extracted so `Settings` stays under the complexity cap once the
 *  Dashboards trigger became conditional. */
function SettingsTabsList({
  isSuperAdmin,
  canManageDashboards,
}: {
  isSuperAdmin: boolean;
  canManageDashboards: boolean;
}) {
  return (
    <TabsList className="mb-6">
      <TabsTrigger value="users">User Management</TabsTrigger>
      {canManageDashboards && <TabsTrigger value="dashboards">Dashboards</TabsTrigger>}
      <TabsTrigger value="properties">Properties</TabsTrigger>
      {isSuperAdmin && <TabsTrigger value="config">Configuration</TabsTrigger>}
      {isSuperAdmin && <TabsTrigger value="google-auth">Google Auth</TabsTrigger>}
      {isSuperAdmin && <TabsTrigger value="spark">SimplySpark</TabsTrigger>}
      <TabsTrigger value="howto">How To</TabsTrigger>
    </TabsList>
  );
}

const Settings = () => {
  const { tenantPath, currentTenantId } = useTenant();
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  const { isAdmin, isSuperAdmin, isLoading: roleLoading } = useUserRole();
  const { canManageDashboards } = useCapabilities();

  // eslint-disable-next-line @typescript-eslint/prefer-nullish-coalescing -- intentional: empty `?tab=` query falls through to "users"
  const defaultTab = searchParams.get("tab") || "users";

  useEffect(() => {
    if (!roleLoading && !isAdmin) {
      void navigate(tenantPath("/"));
    }
  }, [isAdmin, roleLoading, navigate, tenantPath]);

  if (roleLoading) {
    return (
      <div className="min-h-screen bg-background flex items-center justify-center">
        <p className="text-muted-foreground text-sm">Loading...</p>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-background flex flex-col">
      <AppHeader />
      <main className="flex-1 p-10 max-w-4xl mx-auto w-full">
        <Link
          to={tenantPath("/")}
          className="inline-flex items-center gap-2 text-sm text-muted-foreground hover:text-foreground mb-8"
        >
          <ArrowLeft className="h-4 w-4" />
          Back to Dashboards
        </Link>
        <h1 className="text-2xl font-semibold mb-6">Settings</h1>

        <Tabs defaultValue={defaultTab} onValueChange={(val) => setSearchParams({ tab: val })}>
          <SettingsTabsList isSuperAdmin={isSuperAdmin} canManageDashboards={canManageDashboards} />

          <TabsContent value="users">
            <UserManagement />
          </TabsContent>

          {/* SIM-3113/SIM-3115: the Dashboards tab is pure dashboard CRUD, so it
              follows the `dashboards.manage` capability the server reports.
              Assigning dashboard ACCESS lives under Users -> Groups and is
              unaffected. */}
          {canManageDashboards && (
            <TabsContent value="dashboards">
              <DashboardManagement />
            </TabsContent>
          )}

          <TabsContent value="properties">
            <PropertyManagement />
          </TabsContent>

          <TabsContent value="howto">
            <SettingsHowTo />
          </TabsContent>

          {isSuperAdmin && (
            <TabsContent value="config">
              <SettingsConfigTab />
            </TabsContent>
          )}

          {isSuperAdmin && (
            <TabsContent value="spark">
              <div className="max-w-5xl">
                {currentTenantId && <SparkConfigSection tenantId={currentTenantId} />}
              </div>
            </TabsContent>
          )}

          {isSuperAdmin && (
            <TabsContent value="google-auth">
              <div className="max-w-5xl">
                {currentTenantId && <GoogleConnectionSection tenantId={currentTenantId} />}
              </div>
            </TabsContent>
          )}
        </Tabs>
      </main>
    </div>
  );
};

export default Settings;
